import React from 'react';
import { Shield, Zap, Target, TrendingUp, AlertTriangle, CheckCircle, Users, Award, Lightbulb, Clock, DollarSign, BarChart3, Eye, Rocket, Lock, Filter, Play, ArrowRight, Star, Brain, ThumbsUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

const WhyArcRefineryPage = () => {
  const navigate = useNavigate();

  const differentiators = [
    {
      icon: Filter,
      title: "Pre-Market Quality Gate",
      description: "Unlike post-campaign analytics that show you what failed AFTER you've spent money, ArcRefinery acts as your quality gate BEFORE launch.",
      highlight: "Catch issues before spending a dollar on distribution"
    },
    {
      icon: Shield,
      title: "Preventive, Not Reactive",
      description: "Traditional tools analyze performance after ads run. We prevent poor performance by identifying weak creative before it reaches your audience.",
      highlight: "Stop bad ads at the source"
    },
    {
      icon: Brain,
      title: "Framework-First Analysis",
      description: "We don't just give you metrics—we evaluate against 7 proven marketing frameworks that have driven billions in sales.",
      highlight: "Scientific validation, not guesswork"
    },
    {
      icon: Clock,
      title: "Instant Pre-Flight Check",
      description: "Get comprehensive analysis in seconds, not days. No need to run expensive A/B tests to find out your creative is broken.",
      highlight: "Know before you go"
    }
  ];

  const traditionalVsArcRefinery = [
    {
      category: "Traditional Performance Tools",
      icon: AlertTriangle,
      color: "red",
      points: [
        "Analyze AFTER campaigns launch",
        "Tell you what failed, not why",
        "Require significant ad spend to gather data",
        "Focus on metrics, not creative quality",
        "No framework-based validation",
        "Reactive approach to optimization"
      ]
    },
    {
      category: "ArcRefinery Approach",
      icon: Shield,
      color: "green",
      points: [
        "Analyze BEFORE campaigns launch",
        "Explain exactly why creative will succeed or fail",
        "Zero ad spend required for validation",
        "Deep creative quality assessment",
        "7 proven marketing frameworks",
        "Proactive prevention of failure"
      ]
    }
  ];

  const problemScenarios = [
    {
      title: "The Brand Team Problem",
      icon: Award,
      scenario: "Your team spent $50K producing a video ad. You're excited to launch. But what if the hook is weak? What if the CTA isn't clear? You won't know until you've spent another $100K on media.",
      solution: "ArcRefinery reviews your video in 60 seconds and tells you exactly what will work and what won't—before launch.",
      savings: "Save $100K+ in wasted media spend"
    },
    {
      title: "The Agency Dilemma",
      icon: Users,
      scenario: "You've created 5 video concepts for a client. Which one will perform best? Traditional testing requires running all 5 and burning budget to find out.",
      solution: "Upload all 5 to ArcRefinery, get detailed scores for each, and confidently recommend the winner before spending client budget.",
      savings: "Deliver better results, justify your fees"
    },
    {
      title: "The Creator Challenge",
      icon: Target,
      scenario: "You know how to create engaging content, but struggle with clear direction on what makes a video ad truly impactful. Without guidance on proven marketing frameworks, you're guessing what brands want.",
      solution: "ArcRefinery provides clear direction on marketing frameworks and specific elements to improve, helping you create more impactful videos that attract brands and secure long-term partnerships.",
      savings: "Create better videos, work with more brands"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: "Upload Your Video",
      description: "No setup, no integration, no lengthy onboarding. Just upload your video file.",
      icon: Play
    },
    {
      step: 2,
      title: "AI Framework Analysis",
      description: "Our Gemini-powered AI evaluates your video against 7 proven marketing frameworks in seconds.",
      icon: Brain
    },
    {
      step: 3,
      title: "Get Actionable Insights",
      description: "Receive detailed scores, specific weaknesses, and concrete recommendations for improvement.",
      icon: Lightbulb
    },
    {
      step: 4,
      title: "Optimize & Launch Confidently",
      description: "Fix identified issues, re-test if needed, then launch knowing your creative is optimized.",
      icon: Rocket
    }
  ];

  const teamBenefits = [
    {
      team: "Brand Teams",
      icon: Award,
      color: "from-blue-500 to-indigo-500",
      benefits: [
        {
          title: "Protect Brand Reputation",
          description: "Don't let poor creative damage your brand. Validate every video before it represents your company."
        },
        {
          title: "Maximize Marketing Budget",
          description: "Stop wasting 63% of your ad budget on poor creative. Put money only behind proven winners."
        },
        {
          title: "Faster Decision Making",
          description: "Get instant validation instead of waiting weeks for campaign data. Make confident go/no-go decisions."
        },
        {
          title: "Stakeholder Confidence",
          description: "Present data-backed creative recommendations to leadership. No more subjective opinions."
        }
      ]
    },
    {
      team: "Agency Teams",
      icon: Users,
      color: "from-purple-500 to-pink-500",
      benefits: [
        {
          title: "Win More Pitches",
          description: "Show prospects how you validate creative before spending their money. Differentiate with data."
        },
        {
          title: "Reduce Client Testing Costs",
          description: "Pre-screen creative variants before expensive market testing. Deliver better results faster."
        },
        {
          title: "Justify Premium Pricing",
          description: "Demonstrate the rigor of your creative process. Clients pay more for validated excellence."
        },
        {
          title: "Scale Campaign Success",
          description: "Replicate winning formulas across clients. Build a library of what works and why."
        }
      ]
    },
    {
      team: "Content Creators",
      icon: Target,
      color: "from-orange-500 to-red-500",
      benefits: [
        {
          title: "Get Clear Direction on Impactful Videos",
          description: "Stop guessing what works. Receive specific guidance on marketing frameworks and elements to improve in every video you create."
        },
        {
          title: "Create Videos Brands Want",
          description: "Learn exactly what makes video ads impactful. Master the frameworks that brands look for in creator partnerships."
        },
        {
          title: "Attract More Brand Partnerships",
          description: "The more impactful your videos, the more brands want to work with you. Demonstrate professional-level creative understanding."
        },
        {
          title: "Secure Long-Term Collaborations",
          description: "Show brands you consistently deliver performance-driven content. Turn one-time deals into ongoing partnerships with better creative quality."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">The Pre-Market Quality Gate</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Why ArcRefinery Is Different
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8">
              Every other tool tells you what failed after you've spent money. We catch low-quality ads before they ever reach the market.
            </p>
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3 border border-white/20">
              <Lock className="w-5 h-5 mr-3" />
              <span className="font-medium">Catch Bad Ads Before They Cost Money</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Fundamental Difference */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Fundamental Difference
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Traditional performance tools are autopsy reports. ArcRefinery is a health screening.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {differentiators.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
                  <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-blue-700 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {item.highlight}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Traditional vs ArcRefinery Comparison */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center">Traditional Tools vs ArcRefinery</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {traditionalVsArcRefinery.map((column, index) => {
                const Icon = column.icon;
                return (
                  <div key={index}>
                    <div className="flex items-center mb-6">
                      <div className={`w-10 h-10 bg-${column.color}-500 rounded-lg flex items-center justify-center mr-3`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="text-xl font-bold">{column.category}</h4>
                    </div>
                    <ul className="space-y-3">
                      {column.points.map((point, idx) => (
                        <li key={idx} className="flex items-start">
                          {column.color === 'green' ? (
                            <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                          )}
                          <span className="text-gray-300">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Real-World Problem Scenarios */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Real Problems, Real Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how ArcRefinery prevents the costly mistakes teams make every day.
            </p>
          </div>

          <div className="space-y-8">
            {problemScenarios.map((scenario, index) => {
              const Icon = scenario.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-xl border border-indigo-100">
                  <div className="flex items-start mb-6">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{scenario.title}</h3>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          The Problem
                        </h4>
                        <p className="text-red-800 text-sm">{scenario.scenario}</p>
                      </div>
                    </div>

                    <div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          ArcRefinery Solution
                        </h4>
                        <p className="text-green-800 text-sm mb-3">{scenario.solution}</p>
                        <div className="bg-white border border-green-300 rounded-lg p-3">
                          <p className="text-xs font-bold text-green-700 flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            {scenario.savings}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How ArcRefinery Catches Low-Quality Ads */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How We Catch Low-Quality Ads Before Market
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our four-step validation process identifies creative failures before they cost you money.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {howItWorks.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="text-center">
                  <div className="mb-6">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              );
            })}
          </div>

          {/* Technical Deep Dive */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 md:p-12 border border-indigo-200">
            <div className="flex items-start mb-6">
              <Eye className="w-8 h-8 text-indigo-600 mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">What We Analyze</h3>
                <p className="text-gray-700 mb-6">
                  ArcRefinery doesn't just check boxes. We perform deep, multi-layered analysis across every dimension of creative effectiveness:
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 border border-indigo-100">
                <h4 className="font-bold text-gray-900 mb-3">Hook Analysis</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>First 3 seconds effectiveness</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Pattern interruption strength</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Relevance to target audience</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 border border-indigo-100">
                <h4 className="font-bold text-gray-900 mb-3">Story Structure</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Framework alignment (7 models)</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Narrative coherence</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Emotional arc evaluation</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 border border-indigo-100">
                <h4 className="font-bold text-gray-900 mb-3">Conversion Elements</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>CTA clarity and strength</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Value proposition delivery</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Persuasion psychology</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team-Specific Benefits */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for Your Team's Success
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every team has unique challenges. Here's how ArcRefinery solves them.
            </p>
          </div>

          <div className="space-y-12">
            {teamBenefits.map((team, index) => {
              const Icon = team.icon;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                  <div className={`bg-gradient-to-r ${team.color} p-6 md:p-8 text-white`}>
                    <div className="flex items-center">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4">
                        <Icon className="w-8 h-8" />
                      </div>
                      <div>
                        <h3 className="text-2xl md:text-3xl font-bold">{team.team}</h3>
                        <p className="text-white/90">Specialized solutions for your workflow</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 md:p-8">
                    <div className="grid md:grid-cols-2 gap-6">
                      {team.benefits.map((benefit, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-indigo-300 transition-all duration-200">
                          <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                            <ThumbsUp className="w-5 h-5 text-indigo-600 mr-2" />
                            {benefit.title}
                          </h4>
                          <p className="text-gray-600 text-sm">{benefit.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Cost of Skipping Pre-Market Validation
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border border-white/20">
              <div className="text-5xl md:text-6xl font-bold mb-2">63%</div>
              <p className="text-indigo-100">Of ad budgets wasted on poor creative</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border border-white/20">
              <div className="text-5xl md:text-6xl font-bold mb-2">70%</div>
              <p className="text-indigo-100">Of campaign success determined by creative quality</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border border-white/20">
              <div className="text-5xl md:text-6xl font-bold mb-2">60sec</div>
              <p className="text-indigo-100">Time to get comprehensive creative analysis</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Star className="w-16 h-16 text-indigo-600 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Stop Paying to Learn What Doesn't Work
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join the brands, agencies, and creators who validate creative before launch—not after.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            Analyse Your Video Now
          </button>
          <p className="text-sm text-gray-500 mt-6">
            No credit card required • Instant results • Start preventing failures today
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhyArcRefineryPage;

